/**
 * ACHIEVEMENT FAMILIES — rules that EXPAND into achievements, instead of storing each one.
 *
 * The harvest ladder is 5 tiers × 109 plants = 545 achievements, and every one of them has the same shape:
 * "harvest N of plant X, get the tier-N emblem with X's icon on it". Storing 545 near-identical records cost
 * ~322KB and, worse, shipped that to every client on a 60-second poll. A FAMILY stores the RULE — about 250
 * bytes — and the 545 achievements are derived at load.
 *
 * WHAT THIS BUYS, beyond size:
 *   • Adding a plant automatically gets its five achievements. No DB write, no migration, no edit.
 *   • "Every tier-3 badge matches" is structural: they all name the same style, because the rule says so.
 *   • The live tuning blob is left for what it is good at — the handful of achievements you deliberately hand-edit.
 *     A generated achievement can still be overridden by id (see achievements.ts `setAchievementOverrides`), so
 *     nothing is locked away; only the EDITS are stored.
 *
 * Generated ids are STABLE and derived from the rule (`harvested_rose_500`), which is what makes an override
 * possible at all — an id that shifted when the roster changed would orphan every edit and every player's
 * unlocked list on the next deploy. The number in an id is the rung's NOMINAL count, not its threshold: a
 * species' real threshold is scaled for effort (THE EFFORT RULE below), so `harvested_glassvine_10000` needs
 * ~1,950 — and the id stays put however often that scaling is retuned.
 *
 * EVERY GENERATED ACHIEVEMENT STATES ITS CATEGORY (`plants` / `fish` / `insects`), because a family knows exactly
 * what it is about — that is the one thing a rule has over a hand-written list. This is what makes all 1,245
 * generated achievements filterable without anyone labelling them one at a time. `categoryOf` in achievements.ts would infer
 * the same answer from the metric; stating it costs a word and means the inference is a safety net for
 * dev-authored achievements rather than the mechanism the whole ladder depends on.
 *
 * A FAMILY — the rungs of one species ladder — is recovered by achievements.ts `familyOf`, which groups on
 * the parameterized metric (`harvested:rose`) rather than by taking the ids back apart. Nothing about the
 * ids changed to support it, and nothing may: see the stability note above.
 */

import { ALL_PLANT_IDS, baseGrowSeconds, flower, type FlowerId } from "./flowers";
import { FISH, type FishDef } from "./fishing";
import { DEFAULT_FISH_SPAWN_CONFIG, inferFishRarity, secondsPerCast } from "./fishSpawn";
import { DEFAULT_SPAWN_CONFIG, INSECTS, inferRarity, type InsectDef } from "./insects";
import { MAX_RACKS, POTS_PER_RACK } from "./gadgets";
import type { AchievementCategory, AchievementDef, AchievementReward } from "./achievements";

/** One rung of a tiered family: the threshold, the badge style it awards, and what to call it. */
export interface FamilyTier {
  /** The rung's NOMINAL count — the number in its id, and the threshold of the FASTEST species. Every other
   *  species' threshold is derived from it by `ladderCounts`, so read a real threshold off the achievement's
   *  condition, never off this. */
  n: number;
  /** Achievement points this rung is worth (1..10). */
  points: number;
  /** Which BADGE_STYLES entry this rung's badge uses — the reason same-tier badges match across species. */
  style: string;
  /** Rank word used in the generated name ("Rose Gatherer"). */
  title: string;
}

// ---------------------------------------------------------------------------
//  EFFORT — every species' rung costs the same HOURS, so the counts differ
// ---------------------------------------------------------------------------

/**
 * THE EFFORT RULE. A rung is a budget of HOURS, not a count. Each family names how long its top rung takes; a
 * lower rung takes the share of that budget its nominal count is of the top one (harvest 500 is 5% of 10,000,
 * so 5% of the hours); and the count is that budget × how many of THIS species a player gets per hour. So the
 * Glassvine Legend and the Daisy Legend are the same 12 hours of greenhouse, where they were 62 h against 11 h;
 * and a legendary insect's top rung is 13 catches, where 250 sat ~1,100 h of netting away.
 *
 * NEVER ABOVE THE NOMINAL COUNT. The budgets are sized so the fastest species lands at or just over the old
 * flat count, and the cap holds it there. No threshold went UP when this replaced the flat ladders — every one
 * that moved went DOWN, which can only unlock — so no saved unlock is orphaned and no progress bar slides back.
 *
 * THE RATES ARE A MODEL, read from the shipped defaults so retuning those moves the ladders with them:
 * neutral-garden numbers with no boosts, gear or drivers (they speed a whole tier alike) and no condition gates
 * (per-species prose, not a rate). What matters is the RATIO between species — grow time for plants, the tier's
 * share of the bites / visits for catches — and that is what these get right.
 */
export const HARVEST_TOP_HOURS = 12;   // one species in every pot: Daisy 10,800 → held at 10,000
export const FISH_TOP_HOURS = 30;      // a common's share of its water's bites ≈ 8.6/h → 259 → held at 250
export const INSECT_TOP_HOURS = 60;    // a common's share of the visits ≈ 4.4/h → 264 → held at 250

/** Harvests per hour of one species with every greenhouse pot (5 racks × 12) on it, at base grow time. */
export function harvestsPerHour(id: FlowerId): number {
  return (POTS_PER_RACK * MAX_RACKS * 3600) / baseGrowSeconds(flower(id));
}

/** Fish of one species per hour of casting: casts/hour × its tier's share of its own water's bites. Both
 *  habitats carry the same tier ladder; each is still summed on its own so a lopsided roster edit stays honest. */
export function fishPerHour(d: FishDef): number {
  const pct = DEFAULT_FISH_SPAWN_CONFIG.tierBasePct;
  let water = 0;
  for (const f of FISH) if (f.habitat === d.habitat || f.habitat === "both" || d.habitat === "both") water += pct[inferFishRarity(f.weight)];
  return ((3600 / secondsPerCast()) * pct[inferFishRarity(d.weight)]) / Math.max(1e-9, water);
}

/** Visits of one insect species per hour: spawn attempts/hour (gap midpoint) × its tier's share of the roster.
 *  The AUTHORED tier — not `rarityOf`, which an admin override can move after the ladder was built. */
export function insectsPerHour(d: InsectDef): number {
  const cfg = DEFAULT_SPAWN_CONFIG;
  const tierOf = (x: InsectDef) => x.rarity ?? inferRarity(x.weight);
  let all = 0;
  for (const x of INSECTS) all += cfg.tierBasePct[tierOf(x)];
  return ((3600 / ((cfg.gapMinS + cfg.gapMaxS) / 2)) * cfg.tierBasePct[tierOf(d)]) / Math.max(1e-9, all);
}

/** A count rounded to a number a player reads as deliberate (13, 45, 180, 1,800), never below 1. */
function niceCount(v: number): number {
  const step = v < 20 ? 1 : v < 100 ? 5 : v < 500 ? 10 : v < 2000 ? 50 : v < 5000 ? 100 : 500;
  return Math.max(1, Math.round(v / step) * step);
}

/** One species' real thresholds: each rung's share of `topHours` × `perHour`, rounded, capped at the nominal
 *  count (THE EFFORT RULE), and kept strictly rising so two rungs never collapse into one. */
export function ladderCounts(tiers: readonly FamilyTier[], topHours: number, perHour: number): number[] {
  const topN = tiers[tiers.length - 1]!.n;
  let prev = 0;
  return tiers.map((t) => (prev = Math.max(prev + 1, Math.min(t.n, niceCount(topHours * (t.n / topN) * perHour)))));
}

/**
 * THE HARVEST LADDER. Five rungs, each a visibly bigger deal than the last. The badge is the PLANT'S OWN ART
 * wrapped in stacked strokes (the r1..r5 styles, sim/badges BadgeOutline): bronze → +silver → +gold →
 * +mythic violet → +rainbow, each rank adding its ring outside the ones below. (These were bronze shield →
 * mythic rosette emblems with the icon placed on them; an asset never sat right inside a fixed shape.)
 * The counts are the FASTEST plant's (Daisy, 240 s); a slower plant's scale down with its grow time so every
 * Legend is the same `HARVEST_TOP_HOURS` (Glassvine, 1,338 s: 5 / 19 / 95 / 190 / 1,950).
 */
export const HARVEST_TIERS: FamilyTier[] = [
  { n: 25,    style: "r1", title: "Sprout",     points: 1 },
  { n: 100,   style: "r2", title: "Gatherer",   points: 2 },
  { n: 500,   style: "r3", title: "Cultivator", points: 4 },
  { n: 1000,  style: "r4", title: "Master",     points: 6 },
  { n: 10000, style: "r5", title: "Legend",     points: 10 },
];

/** The metric family name for per-plant harvest counts. Kept here so the generator and the counter that
 *  feeds it (App.tsx `recordHarvest`) cannot drift apart. */
export const HARVEST_METRIC = "harvested";

/** The stable id for one generated harvest achievement. `n` is the rung's NOMINAL count (`HARVEST_TIERS[i].n`),
 *  never the species' scaled threshold — that is what keeps the id fixed when the scaling is retuned. */
export function harvestAchievementId(plant: FlowerId, n: number): string {
  return `${HARVEST_METRIC}_${plant}_${n}`;
}

/**
 * Expand the per-plant harvest ladder into achievement definitions.
 *
 * NO TEXT on the badge: the plant's own icon is the identity, and a number crammed alongside it just makes
 * both harder to read at case size.
 */
export function generateHarvestAchievements(plantIds: readonly FlowerId[] = ALL_PLANT_IDS): AchievementDef[] {
  const out: AchievementDef[] = [];
  for (const id of plantIds) {
    let sp;
    try { sp = flower(id); } catch { continue; }      // a roster id with no species record: skip, never throw
    if (!sp) continue;
    const counts = ladderCounts(HARVEST_TIERS, HARVEST_TOP_HOURS, harvestsPerHour(id));
    HARVEST_TIERS.forEach((tier, i) => {
      const need = counts[i]!;
      out.push({
        id: harvestAchievementId(id, tier.n),
        category: "plants",
        name: `${sp.name} ${tier.title}`,
        // Phrased "Harvest 25 × Rose" rather than pluralising the species name: the roster is full of names
        // that break naive +s rules (Barrel Cactus, Pampas Grass, Cosmos, String of Pearls, Hens & Chicks),
        // and a pluralisation engine for 109 hand-written names is a worse bug surface than the phrasing.
        desc: ladderDesc("Harvest", need, sp.name),
        icon: sp.icon,
        conditions: [{ metric: `${HARVEST_METRIC}:${id}`, threshold: need }],
        badge: { style: tier.style, icon: `plant:${id}` },
        points: tier.points,
        reward: ladderReward(i, "plant", id, sp.sellValue, counts),
        ...(sp.release ? { release: sp.release } : {}),   // the species' content release (sim/releases.ts)
      });
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
//  THE CATCH LADDER — per-species fish and insect collection
// ---------------------------------------------------------------------------

/**
 * THE CATCH LADDER. Five rungs — nominally 10 / 25 / 50 / 100 / 250 — shared by fish and insects.
 *
 * WHY THE NUMBERS ARE SO MUCH LOWER THAN THE HARVEST LADDER'S. A plant is a factory: put it on a shelf,
 * water it, and 10,000 of one species is a matter of patience. A named fish or insect is not. Each one
 * needs the right water or habitat, the right season, time of day and weather, and then a lure that suits
 * it or a net swing that connects. 250 of ONE common species is the equivalent long haul.
 * The harvest ladder is the reference for the STRUCTURE of this family, never for its thresholds.
 *
 * SCALED BY RARITY (THE EFFORT RULE above). This was one flat ladder on the argument that rarity "prices itself
 * in" — it did, at 20× (base chance 40% common → 2% legendary), which put a legendary's 250 at ~580 h of casting
 * or ~1,100 h of netting while a common took 30. A rung now costs the same HOURS on every species, which is
 * what makes two players' tier-3 badges represent the same work; the COUNT is what differs (a legendary fish:
 * 1 / 2 / 3 / 5 / 13).
 *
 * SAME FIVE STYLES AS THE HARVEST LADDER, by design: a gold-ringed badge is gold-ringed whether it was earned on
 * a rose, a trout or a mantis, so the whole game's achievements read as one set. The top rung landing on the
 * rainbow ring is what makes a finished species feel like a capstone rather than a long errand.
 *
 * POINTS REUSE THE HARVEST LADDER'S 1/2/4/6/10 (23 per species). Measured through `pointsOf` rather than
 * assumed, because this family moves the denominator a long way: 52 hand-written achievements (270 pts earnable
 * by play; the granted backer badges sit outside `maxPoints`) + 109 plants × 23 (2,507) is 2,777; adding 70
 * fish and 70 insects at 23 each (1,610 apiece) puts the maximum at 5,997 across 1,297 achievements — catches
 * 53.7% of it, plants 41.8%, landmarks the remaining 4.5%. Recount
 * these if a roster changes; they are the reason the weighting is defensible, not decoration.
 *
 * That split is intended. Equal weight PER SPECIES is the FLOOR these deserve — each rung here is scarcer
 * per unit than its harvest counterpart — and the 140 catch species simply outnumber the 109 plant species,
 * so the collection half of the game ends up worth slightly more than the growing half. Weighting them
 * below plants was the only clearly wrong answer; going above 10 is impossible anyway (MAX_POINTS clamps
 * it), which is what caps a rung at parity with harvest rather than above it despite the lower counts.
 */
export const CATCH_TIERS_BASE: Omit<FamilyTier, "title">[] = [
  { n: 10,  style: "r1", points: 1 },
  { n: 25,  style: "r2", points: 2 },
  { n: 50,  style: "r3", points: 4 },
  { n: 100, style: "r4", points: 6 },
  { n: 250, style: "r5", points: 10 },
];

/** Exactly one rank word per rung. A tuple rather than an array so a family that supplies four titles or
 *  six is a compile error rather than an achievement named "Bluegill undefined". */
type CatchTitles = readonly [string, string, string, string, string];

/** A rung's description: "Catch 25 × Rainbow Trout.", or "Catch your first Lake Sturgeon." when the rung is
 *  one — a rare species' first rung often is, and "1 ×" reads like a receipt. */
function ladderDesc(verb: string, need: number, name: string): string {
  return need === 1 ? `${verb} your first ${name}.` : `${verb} ${need.toLocaleString()} × ${name}.`;
}

/** Build a catch ladder from the shared rungs plus a family's own rank words. */
function catchTiers(titles: CatchTitles): FamilyTier[] {
  return CATCH_TIERS_BASE.map((t, i) => ({ ...t, title: titles[i] as string }));
}

/**
 * FISH rank words. Chosen to sit after ANY species name, a pond-sized Pumpkinseed or a stream's Silver
 * Minnow as much as a Lake Sturgeon ("Bog Wraith Reelhand", "Largemouth Bass Angler"), and to climb from
 * the first casts to someone who knows that one fish's habits. Every one is a NOUN for a person: the old
 * "Hooked" (an adjective) and "Deepwater" (a place no minnow lives) read wrong on most of the roster.
 * Gardening words would be wrong here — nothing on this ladder is grown.
 */
export const FISH_CAUGHT_TIERS: FamilyTier[] = catchTiers([
  "Caster", "Angler", "Reelhand", "Baitmaster", "Whisperer",
]);

/**
 * INSECT rank words. The netting equivalent: a first sighting, a steady netter, a collector, a field
 * naturalist, and finally the authority on that one species. Each rung must OUTRANK the one below it —
 * the old fourth rung, "Catcher", read as a step down from "Collector".
 */
export const INSECT_CAUGHT_TIERS: FamilyTier[] = catchTiers([
  "Spotter", "Netter", "Collector", "Naturalist", "Authority",
]);

/** The metric family name for per-species fish catches. Kept beside the generator that reads it and the
 *  counter that feeds it (App.tsx `fishCounters`) so the two cannot drift apart. */
export const FISH_CAUGHT_METRIC = "fishCaught";
/** The metric family name for per-species insect catches. Paired with App.tsx `insectCounters`. */
export const INSECT_CAUGHT_METRIC = "insectCaught";

/** The stable id for one generated fish-catch achievement. Derived from the rule, never from roster position —
 *  an id that shifted when a fish was added would orphan every dev-editor override and every player's
 *  unlocked list on the next deploy. */
export function fishCaughtAchievementId(fish: string, n: number): string {
  return `${FISH_CAUGHT_METRIC}_${fish}_${n}`;
}

/** The stable id for one generated insect-catch achievement. Same contract as `fishCaughtAchievementId`. */
export function insectCaughtAchievementId(insect: string, n: number): string {
  return `${INSECT_CAUGHT_METRIC}_${insect}_${n}`;
}

/**
 * Expand the per-species catch ladder into achievement definitions. Shared by both families, because the
 * two differ only in their roster, metric name, icon kind, rank words and category.
 *
 * Phrased "Catch 25 × Rainbow Trout" rather than pluralising: the rosters are full of names that defeat a
 * naive +s ("Bleak", "Gudgeon", "Stained Glass", "Seven-spot Ladybug", "Hummingbird Hawk-moth"), and a
 * pluralisation engine for 140 hand-written names is a worse bug surface than the phrasing.
 */
function generateCatchAchievements<T extends { id: string; name: string; icon: string; value: number; release?: string }>(
  species: readonly T[],
  perHour: (sp: T) => number,
  topHours: number,
  tiers: readonly FamilyTier[],
  metric: string,
  iconKind: "fish" | "insect",
  achievementId: (id: string, n: number) => string,
  verb: string,
  category: AchievementCategory,
): AchievementDef[] {
  const out: AchievementDef[] = [];
  for (const sp of species) {
    const counts = ladderCounts(tiers, topHours, perHour(sp));
    tiers.forEach((tier, i) => {
      const need = counts[i]!;
      out.push({
        id: achievementId(sp.id, tier.n),
        category,
        name: `${sp.name} ${tier.title}`,
        desc: ladderDesc(verb, need, sp.name),
        icon: sp.icon,
        conditions: [{ metric: `${metric}:${sp.id}`, threshold: need }],
        badge: { style: tier.style, icon: `${iconKind}:${sp.id}` },
        points: tier.points,
        reward: ladderReward(i, iconKind, sp.id, sp.value, counts),
        ...(sp.release ? { release: sp.release } : {}),   // the species' content release (sim/releases.ts)
      });
    });
  }
  return out;
}

/** Expand the per-fish catch ladder — 5 rungs × 70 species. */
export function generateFishCaughtAchievements(species: readonly FishDef[] = FISH): AchievementDef[] {
  return generateCatchAchievements(species, fishPerHour, FISH_TOP_HOURS,
    FISH_CAUGHT_TIERS, FISH_CAUGHT_METRIC, "fish", fishCaughtAchievementId, "Catch", "fish");
}

/** Expand the per-insect catch ladder — 5 rungs × 70 species. */
export function generateInsectCaughtAchievements(species: readonly InsectDef[] = INSECTS): AchievementDef[] {
  return generateCatchAchievements(species, insectsPerHour, INSECT_TOP_HOURS,
    INSECT_CAUGHT_TIERS, INSECT_CAUGHT_METRIC, "insect", insectCaughtAchievementId, "Collect", "insects");
}

/**
 * THE COIN RUNG'S SHARE of what that rung's own count sells for. Harvest pays a quarter of the GROSS because
 * replanting costs ≈0.38× sellValue, so a quarter of gross is ≈40% of the profit; a catch costs nothing to
 * re-land, so it pays 40% straight. Either way the purse is a real bonus on the work it marks — the old 5×
 * value was ~1% of what 500 harvests had sold for. That old 5× stays as the FLOOR: a legendary's rung is now
 * 3 catches rather than 50, and the share of 3 would otherwise pay less than the rung used to (Koi 310 vs 1,300).
 */
const COIN_RUNG_SHARE = { plant: 0.25, fish: 0.4, insect: 0.4 } as const;

/** Coins rounded to a figure that reads as a purse, not a calculation (180, 2,350, 29,200). */
function niceCoins(v: number): number {
  const step = v < 1000 ? 10 : v < 10000 ? 50 : 100;
  return Math.max(10, Math.round(v / step) * step);
}

/**
 * THE LADDER REWARDS — the same five-rung shape for every species, filled with that species' own values:
 *   1 · a start: 10 seeds of the plant, or a little Fishing / Bug Catching XP
 *   2 · a BOTANICAL MARKER for that species (a trophy: granted once, never sold, never re-obtainable)
 *   3 · coins — `COIN_RUNG_SHARE` of what this rung's count of the species sells for (never under 5× value)
 *   4 · petals — `LADDER_PETALS[kind][0]`
 *   5 · a GOLD STATUE of that species (a trophy) + petals — `LADDER_PETALS[kind][1]`; plants keep the legend sparkle
 * Trophies ride in `decor` under their per-species bag key ("goldStatue:plant:rose", sim/decor.ts decorKey);
 * the grant path caps a trophy at one however it arrives.
 *
 * PETALS FOLLOW THE RUNG'S HOURS (THE EFFORT RULE), not its points — every top rung is 10 points, but a plant
 * Legend is HARVEST_TOP_HOURS (12) of a greenhouse that mostly runs itself, a fish 30 h of casting and an insect
 * 60 h of netting. At ~6–8 petals per focused hour they sit beside the hand-written landmarks (a level-50 track,
 * ~35 h, pays 400). The ladder is 249 species deep, so these are deliberately below the landmark curve: the whole
 * generated set pays ≈ 61k petals over thousands of hours, where landmark-sized payouts would pay ~125k.
 * Each stays under the hand-written ceiling (achievements.ts THE PETALS FOLLOW THE POINTS: < 2,062 / 3 per grant)
 * and the server's MAX_ACHIEVEMENT_PETALS.
 */
const LADDER_PETALS = {
  plant:  [25, 100],   // Master 1.2 h · Legend 12 h of greenhouse
  fish:   [75, 200],   // Baitmaster 12 h · Whisperer 30 h of casting
  insect: [100, 300],  // Naturalist 24 h · Authority 60 h of netting
} as const;

function ladderReward(rung: number, kind: "plant" | "fish" | "insect", id: string, value: number, counts: readonly number[]): AchievementReward | undefined {
  const ref = `${kind}:${id}`;
  switch (rung) {
    case 0: return kind === "plant" ? { seeds: { [id]: 10 } as AchievementReward["seeds"] } : kind === "fish" ? { fishXp: 60 } : { bugXp: 40 };
    case 1: return { decor: { [`botanicalMarker:${ref}`]: 1 } };
    case 2: return { coins: niceCoins(Math.max(value * 5, (counts[2] ?? 0) * value * COIN_RUNG_SHARE[kind])) };
    case 3: return { petals: LADDER_PETALS[kind][0] };
    case 4: return { decor: { [`goldStatue:${ref}`]: 1 }, petals: LADDER_PETALS[kind][1] };
    default: return undefined;
  }
}

/** Every generated achievement, from every family. One call site so a new family is added in exactly one place. */
export function generatedAchievements(): AchievementDef[] {
  return [...generateHarvestAchievements(), ...generateFishCaughtAchievements(), ...generateInsectCaughtAchievements()];
}
